import {
  useAction,
  useWatchableValue,
  Watchable,
} from '@cotera/client/v0/actions/framework';
import { times } from 'lodash';
import {
  CellComponentProps,
  Column,
  DataGrid,
  DataGridBody,
  DataGridTopBarContainer,
  HeaderComponentProps,
} from '../components/data-grid';
import { DefaultHeader } from '../components/data-grid/header-renderer';
import { Skeleton } from '../components/ui/skeleton';
import { Button, Text } from '@cotera/client/app/components/ui';
import { DropdownMenuItem } from '../components/ui/dropdown-menu';
import { useMemo } from 'react';
import { OpenEditColumnAction } from '../dataset/actions/open-edit-column.action';

const SkeletonHeader: React.FC<
  HeaderComponentProps & { columns: Watchable<Column[]> }
> = (props) => {
  const openEdit = useAction(OpenEditColumnAction);

  const actions = (
    <>
      {props.column.t !== 'assumption' && (
        <DropdownMenuItem asChild>
          <Button
            inline
            icon="edit"
            onClick={async () => {
              await openEdit.execute({ name: props.column.key });
            }}
          >
            Edit
          </Button>
        </DropdownMenuItem>
      )}
      {props.column.status !== 'hidden' && (
        <DropdownMenuItem asChild>
          <Button
            inline
            icon="eye-slash"
            onClick={() => {
              props.columns.set(
                props.columns
                  .snapshot()
                  .map((c) =>
                    c.key === props.column.key ? { ...c, status: 'hidden' } : c
                  )
              );
            }}
          >
            Hide
          </Button>
        </DropdownMenuItem>
      )}
    </>
  );

  return <DefaultHeader {...props} className="bg-white" actions={actions} />;
};

const SkeletonCell: React.FC<CellComponentProps> = () => (
  <div className="w-full h-full flex items-center justify-center p-3">
    <Skeleton className="w-full h-full" />
  </div>
);

const headerHeight = Watchable.fromValue(36);

export const SkeletonDataGrid: React.FC<{
  children?: React.ReactNode;
  columns: Watchable<Column[]>;
}> = ({ columns, children }) => {
  const HeaderRenderer = useMemo(() => {
    return (props: HeaderComponentProps) => {
      return <SkeletonHeader {...props} columns={columns} />;
    };
  }, [columns]);

  return (
    <DataGrid
      model={{
        columns,
        focusedCell: Watchable.fromValue<{
          row: number;
          col: number;
          columnKey: string;
          rowData: any;
        } | null>(null),
        selectedRows: Watchable.fromValue<Set<number>>(new Set()),
        selectionAnchor: Watchable.fromValue<number | null>(null),
      }}
      headerHeight={headerHeight}
      rowHeight={36}
      HeaderComponent={HeaderRenderer}
      body={children}
      topBar={() => (
        <DataGridTopBarContainer className="h-10">
          <div className="flex items-center gap-2">
            <Text.Caption>Loading...</Text.Caption>
          </div>
        </DataGridTopBarContainer>
      )}
    />
  );
};

export const SkeletonDataGridBody = ({
  columns,
}: {
  columns: Watchable<Column[]>;
}) => {
  const columnsSnapshot = useWatchableValue(columns);

  return (
    <DataGridBody
      hasMore={false}
      data={times(50, (i) => ({
        ...Object.fromEntries(columnsSnapshot.map((c) => [c.key, null])),
        rowNum: String(i),
      }))}
      CellComponent={SkeletonCell}
    />
  );
};
